import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  faCheck,
  faCircleNotch,
  faExclamationCircle,
  faPause,
  faStop,
} from "@fortawesome/pro-regular-svg-icons";
import { isEqual } from "lodash-es";
import { AnimatePresence, m } from "motion/react";
import { Control, Controller, useForm } from "react-hook-form";

import type {
  ActiveSubstatus,
  AgentPlaygroundFormValues,
  PlaygroundSessionState,
  RuntimeByCategory,
  StoppedSubstatus,
} from "src/api/agents/execution";
import { Schema } from "src/api/flowTypes";
import { TokensPill } from "src/base-components/agents/MetricPills";
import { PlaygroundFileListField } from "src/base-components/agents/PlaygroundFileListField";
import {
  AccordionDisclosure,
  CollapsiblePre,
  computeToolCallStatus,
  ToolCallAccordionItem,
} from "src/base-components/agents/ToolCallDisplay";
import { TOOL_ICON_CONFIG } from "src/base-components/agents/ToolCard";
import { CodeEditor } from "src/base-components/CodeInput/CodeEditor";
import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { FormItem } from "src/base-components/FormItem";
import { Input } from "src/base-components/Input";
import { MarkdownRenderer } from "src/base-components/MarkdownRenderer/MarkdownRenderer";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import { Textarea } from "src/base-components/Textarea";
import type {
  AIMessage,
  HumanMessage,
  LiveStatusDetailKind,
  SessionMessagesResponseMessagesInner,
  SessionResultResponse,
  ToolCallContentBlock,
  ToolMessage,
} from "src/clients/agent-management";
import { TimekeepingCategory, ToolType } from "src/clients/agent-management";
import { Callout } from "src/design-system/Callout";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { EnumValueObject } from "src/schema/schemaMappingUtils";
import {
  AgentSchemaPropertyUI,
  convertAgentSchemaBEtoUI,
} from "src/utils/agents/agentSchemaUtils";
import { utcDateToLocalDate } from "src/utils/datetime";
import { formatDuration } from "src/utils/numbers";

/**
 * Token count for the first human message (the user's invocation).
 * Returns null when no human message has been recorded yet.
 */
export const getInputTokenCount = (
  messages: SessionMessagesResponseMessagesInner[],
): number | null => {
  const firstHumanMessage = messages.find((m) => m.type === "human") as
    | HumanMessage
    | undefined;
  return firstHumanMessage?.token_count ?? null;
};

/**
 * Token count for the last AI message (the agent's final response).
 * Returns null when no AI message has been recorded yet.
 */
export const getOutputTokenCount = (
  messages: SessionMessagesResponseMessagesInner[],
): number | null => {
  const lastAiMessage = messages.findLast((m) => m.type === "ai") as
    | AIMessage
    | undefined;
  return lastAiMessage?.token_count ?? null;
};

type ToolCallWithResult = {
  toolCall: ToolCallContentBlock;
  toolResult: ToolMessage | null;
};

export type ProcessedDisplayItem =
  | { kind: "ai-text"; id: string; content: string }
  | {
      kind: "tool-group";
      id: string;
      toolCallsWithResults: ToolCallWithResult[];
    };

/**
 * Pure session-messages → display-items processing (exported for testing).
 *
 * On success the final structured-output submission (json_response) is the agent
 * output, shown separately in the "Agent output" accordion, so it is hidden
 * here. Earlier (re-driven) structured-output attempts, and every attempt in a
 * failed run, stay visible so failed output attempts remain inspectable.
 */
export const buildProcessedItems = (
  sessionMessages: SessionMessagesResponseMessagesInner[],
  result: SessionResultResponse | null,
): ProcessedDisplayItem[] => {
  // Build lookup: tool_call_id → ToolMessage
  const toolResultMap = new Map<string, ToolMessage>();
  for (const msg of sessionMessages) {
    if (msg.type === "tool") {
      toolResultMap.set(msg.tool_call_id, msg);
    }
  }

  // On success, hide just the last structured-output tool call (the submission
  // shown as the agent output). Failed/re-driven attempts stay visible.
  let hiddenToolCallId: string | null = null;
  if (result) {
    for (const msg of sessionMessages) {
      if (msg.type !== "ai") continue;
      for (const block of msg.content_blocks ?? []) {
        if (
          block.type === "tool_call" &&
          (block as ToolCallContentBlock).metadata.type ===
            ToolType.STRUCTURED_OUTPUT
        ) {
          hiddenToolCallId = block.id;
        }
      }
    }
  }

  const items: ProcessedDisplayItem[] = [];

  for (const msg of sessionMessages) {
    if (msg.type !== "ai") continue;

    const blocks = msg.content_blocks ?? [];

    for (const [i, block] of blocks.entries()) {
      if (block.type === "text" && block.text) {
        items.push({
          kind: "ai-text",
          id: `${msg.id}-${i}`,
          content: block.text,
        });
      }
    }

    const toolCalls = blocks
      .filter(
        (b) =>
          b.type === "tool_call" &&
          (b as ToolCallContentBlock).id !== hiddenToolCallId &&
          (b as ToolCallContentBlock).metadata.type in TOOL_ICON_CONFIG,
      )
      .map((b) => b as ToolCallContentBlock);

    if (toolCalls.length > 0) {
      items.push({
        kind: "tool-group",
        id: `${msg.id}-tools`,
        toolCallsWithResults: toolCalls.map((tc) => ({
          toolCall: tc,
          toolResult: toolResultMap.get(tc.id) ?? null,
        })),
      });
    }
  }

  // Deduplicate: hide last AI text if it matches the agent output
  if (result && items.length > 0) {
    const lastItem = items[items.length - 1];
    if (lastItem.kind === "ai-text") {
      const trimmed = lastItem.content.trim();
      const matchesText = result.text != null && trimmed === result.text.trim();
      let matchesStructured = false;
      if (result.structured_output != null) {
        try {
          const parsed = JSON.parse(trimmed);
          matchesStructured = isEqual(parsed, result.structured_output);
        } catch {
          // Not valid JSON — no match
        }
      }
      if (matchesText || matchesStructured) {
        items.pop();
      }
    }
  }

  return items;
};

const useProcessedMessages = (
  sessionMessages: SessionMessagesResponseMessagesInner[],
  result: SessionResultResponse | null,
): ProcessedDisplayItem[] =>
  useMemo(
    () => buildProcessedItems(sessionMessages, result),
    [sessionMessages, result],
  );

export type ExecutionLogViewProps = {
  sessionState: PlaygroundSessionState;
  testInputHeader: React.ReactNode;
  dataLoc?: string;
};

export const ExecutionLogView: React.FC<ExecutionLogViewProps> = ({
  sessionState,
  testInputHeader,
  dataLoc,
}) => {
  const sessionMessages =
    sessionState.status === "idle" ? [] : sessionState.sessionMessages;

  // Auto-scroll logic for the internal scroll container
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isAtBottomRef = useRef(true);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    isAtBottomRef.current = scrollHeight - scrollTop - clientHeight < 50;
  }, []);

  // Auto-scroll when new messages arrive (only if user was at bottom)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    if (isAtBottomRef.current) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [sessionMessages.length]);

  // Result for the agent output accordion (success only)
  const result =
    sessionState.status === "stopped" && sessionState.substatus === "success"
      ? sessionState.result
      : null;

  const outputTokenCount = getOutputTokenCount(sessionMessages);

  // Process messages into structured display items
  const processedItems = useProcessedMessages(sessionMessages, result);

  // Determine session substatus for tool call status computation
  const activeSubstatus: ActiveSubstatus | null =
    sessionState.status === "active" ? sessionState.substatus : null;

  // Count tool calls awaiting results (paused tools)
  const pausedToolCount = useMemo(() => {
    if (activeSubstatus !== "paused") return 0;
    return processedItems.reduce((count, item) => {
      if (item.kind !== "tool-group") return count;
      return (
        count + item.toolCallsWithResults.filter((t) => !t.toolResult).length
      );
    }, 0);
  }, [processedItems, activeSubstatus]);

  // Show status display only for active states (all stopped states use top banners)
  const showStatusDisplay = sessionState.status === "active";

  return (
    <div className="flex flex-1 flex-col overflow-hidden" data-loc={dataLoc}>
      <div
        ref={scrollContainerRef}
        className="decideScrollbar flex-1 overflow-y-auto pb-5"
        onScroll={handleScroll}
      >
        {sessionState.status !== "idle" && (
          <TraceInfoBanner runtimeByCategory={sessionState.runtimeByCategory} />
        )}
        <div className="mt-4">{testInputHeader}</div>
        {sessionState.status !== "idle" && (
          <div className="space-y-3 py-4">
            {/* Agent messages */}
            {processedItems.map((item) => (
              <m.div
                key={item.id}
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
              >
                {item.kind === "ai-text" ? (
                  <MarkdownRenderer content={item.content} />
                ) : (
                  <ToolCallGroup
                    item={item}
                    sessionSubstatus={activeSubstatus}
                  />
                )}
              </m.div>
            ))}

            {/* Session status display (thinking, paused) */}
            {showStatusDisplay && (
              <SessionStatusDisplay
                liveStatusKind={sessionState.liveStatusDetail?.kind ?? null}
                pausedToolCount={pausedToolCount}
                sessionStatus={sessionState}
              />
            )}

            {/* Agent output accordion (shown on success) */}
            {result && (
              <m.div
                animate={{ opacity: 1 }}
                initial={{ opacity: 0 }}
                transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
              >
                <AccordionDisclosure
                  rightSlot={
                    <div className="flex items-center gap-1.5">
                      {outputTokenCount != null && (
                        <TokensPill count={outputTokenCount} />
                      )}
                      <AgentOutputStatusPill substatus="success" />
                    </div>
                  }
                  title="Agent output"
                  defaultOpen
                >
                  <AgentOutputAccordionBody result={result} />
                </AccordionDisclosure>
              </m.div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

type ReadonlySubmittedInputAccordionProps = {
  submittedInput: AgentPlaygroundFormValues;
  inputSchema: Record<string, unknown> | null;
  title?: string;
  dataLoc?: string;
  tokenCount?: number | null;
};

/**
 * Read-only variant of TestInputAccordion that wires up a form from a past
 * session's `submittedInput`. Array/object values are stringified so they
 * render in the CodeEditor fields; file/date/datetime values are passed
 * through unchanged because their fields consume native types.
 */
export const ReadonlySubmittedInputAccordion: React.FC<
  ReadonlySubmittedInputAccordionProps
> = ({
  submittedInput,
  inputSchema,
  title,
  dataLoc = "readonly-test-input-accordion",
  tokenCount,
}) => {
  const properties = useMemo(
    () =>
      convertAgentSchemaBEtoUI(inputSchema as Schema | null)?.properties ?? [],
    [inputSchema],
  );

  const stringifiedInput = useMemo((): AgentPlaygroundFormValues => {
    const excludedFieldNames = new Set(
      properties
        .filter((p) => {
          const type = p.type?.[0];
          return type === "file[]" || type === "date" || type === "datetime";
        })
        .map((p) => p.fieldName),
    );
    return {
      ...submittedInput,
      structuredInput: Object.fromEntries(
        Object.entries(submittedInput.structuredInput).map(([key, value]) =>
          typeof value === "object" &&
          value !== null &&
          !excludedFieldNames.has(key)
            ? [key, JSON.stringify(value)]
            : [key, value],
        ),
      ),
    };
  }, [submittedInput, properties]);

  // The invocation is only persisted once the session's first turn completes,
  // so a session opened while it is still starting mounts with empty values and
  // has to pick them up on a later poll. `values` keeps the form in sync with
  // them; `defaultValues` would freeze the empty snapshot until a page reload.
  const readonlyForm = useForm<AgentPlaygroundFormValues>({
    values: stringifiedInput,
  });

  return (
    <TestInputAccordion
      control={readonlyForm.control}
      dataLoc={dataLoc}
      properties={properties}
      title={title}
      tokenCount={tokenCount}
      disabled
      defaultOpen={false}
    />
  );
};

type TestInputAccordionProps = {
  properties: AgentSchemaPropertyUI[];
  control: Control<AgentPlaygroundFormValues>;
  disabled: boolean;
  expanded?: boolean;
  onExpandedChange?: (v: boolean) => void;
  dataLoc: string;
  title?: string;
  showErrors?: boolean;
  defaultOpen: boolean;
  tokenCount?: number | null;
};

export const TestInputAccordion: React.FC<TestInputAccordionProps> = ({
  properties,
  control,
  disabled,
  expanded,
  onExpandedChange,
  dataLoc,
  title = "Test input",
  showErrors = false,
  defaultOpen,
  tokenCount,
}) => {
  return (
    <AccordionDisclosure
      dataLoc={dataLoc}
      open={expanded}
      title={title}
      defaultOpen={defaultOpen}
      onOpenChange={onExpandedChange}
      rightSlot={
        tokenCount != null ? <TokensPill count={tokenCount} /> : undefined
      }
    >
      <div className="space-y-4 px-3 pb-3">
        {properties.length > 0 && (
          <div className="rounded-lg bg-gray-50 px-4 py-3">
            {properties.map((property) => (
              <FormItem key={property.fieldName} gap="xs">
                <FormItem.Label isRequired={property.required}>
                  {property.fieldName}
                </FormItem.Label>
                <PlaygroundInputField
                  control={control}
                  disabled={disabled}
                  fieldPath={`structuredInput.${property.fieldName}`}
                  property={property}
                  showErrors={showErrors}
                />
              </FormItem>
            ))}
          </div>
        )}
        <div className="rounded-lg bg-gray-50 px-4 py-3">
          <FormItem gap="xs">
            <FormItem.Label>Agent instructions (optional)</FormItem.Label>
            <Controller
              control={control}
              name="instructions"
              render={({ field }) => (
                <Textarea
                  data-loc="test-input-instructions"
                  disabled={disabled}
                  placeholder="Write test instructions for your Agent"
                  value={field.value}
                  fullWidth
                  resizeable
                  onChange={field.onChange}
                />
              )}
            />
          </FormItem>
        </div>
      </div>
    </AccordionDisclosure>
  );
};

type PlaygroundInputFieldProps = {
  property: AgentSchemaPropertyUI;
  control: Control<AgentPlaygroundFormValues>;
  fieldPath: `structuredInput.${string}`;
  disabled?: boolean;
  showErrors?: boolean;
};

const PlaygroundInputField: React.FC<PlaygroundInputFieldProps> = ({
  property,
  control,
  fieldPath,
  disabled = false,
  showErrors = false,
}) => {
  const { fieldName, required } = property;
  const type = property.type?.[0] ?? "string";
  const enumValues = (property.enum as EnumValueObject[] | undefined)?.map(
    (e) => e.value,
  );

  switch (type) {
    case "string":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Input
                data-loc={`test-input-${fieldName}`}
                disabled={disabled}
                placeholder={fieldName}
                value={(field.value as string) ?? ""}
                fullWidth
                onChange={field.onChange}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) =>
                  (value as string)?.trim() !== "" || "Field is required"
              : undefined,
          }}
        />
      );

    case "number":
    case "integer":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Input
                data-loc={`test-input-${fieldName}`}
                disabled={disabled}
                placeholder={fieldName}
                type="number"
                value={(field.value as string) ?? ""}
                fullWidth
                onChange={(e) => {
                  const val = e.target.value;
                  field.onChange(val === "" ? null : Number(val));
                }}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "boolean":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field }) => (
            <div>
              <Switch
                dataLoc={`test-input-${fieldName}`}
                disabled={disabled}
                enabled={(field.value as boolean) ?? false}
                onChange={field.onChange}
              />
            </div>
          )}
        />
      );

    case "date":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <DateTimePickerDropdown
                disabled={disabled}
                placeholder={`Select ${fieldName}`}
                value={utcDateToLocalDate(
                  field.value as Date | string | undefined,
                )}
                allowFutureDates
                dateOnly
                onChange={field.onChange}
                onReset={() => field.onChange(null)}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "datetime":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <DateTimePickerDropdown
                disabled={disabled}
                placeholder={`Select ${fieldName}`}
                value={field.value as Date | undefined}
                allowFutureDates
                onChange={field.onChange}
                onReset={() => field.onChange(null)}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "enum":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <Select
                dataLoc={`test-input-${fieldName}`}
                disabled={disabled}
                options={(enumValues ?? []).map((val) => ({
                  key: val,
                  value: val,
                }))}
                placeholder={`Select ${fieldName}`}
                value={(field.value as string) ?? ""}
                onChange={field.onChange}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) => value != null || "Field is required"
              : undefined,
          }}
        />
      );

    case "array":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <div className="h-32">
                <CodeEditor
                  backgroundColor="white"
                  dataLoc={`test-input-${fieldName}`}
                  disabled={disabled}
                  language="json"
                  value={(field.value as string) ?? ""}
                  lineWrapping
                  onChange={field.onChange}
                />
              </div>
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value) => {
              if (!value || !(value as string).trim()) {
                return !required || "Field is required";
              }
              try {
                const parsed = JSON.parse(value as string);
                if (!Array.isArray(parsed)) {
                  return "Field is not an array";
                }
              } catch {
                return "Field is invalid JSON";
              }
            },
          }}
        />
      );

    case "object":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <div className="h-32">
                <CodeEditor
                  backgroundColor="white"
                  dataLoc={`test-input-${fieldName}`}
                  disabled={disabled}
                  language="json"
                  value={(field.value as string) ?? ""}
                  lineWrapping
                  onChange={field.onChange}
                />
              </div>
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: (value) => {
              if (!value || !(value as string).trim()) {
                return !required || "Field is required";
              }
              try {
                const parsed = JSON.parse(value as string);
                if (
                  typeof parsed !== "object" ||
                  parsed === null ||
                  Array.isArray(parsed)
                ) {
                  return "Field is not an object";
                }
                return true;
              } catch {
                return "Field is invalid JSON";
              }
            },
          }}
        />
      );

    case "file[]":
      return (
        <Controller
          control={control}
          name={fieldPath}
          render={({ field, fieldState }) => (
            <>
              <PlaygroundFileListField
                disabled={disabled}
                field={field}
                fieldName={fieldName}
              />
              {showErrors && fieldState.error && (
                <ErrorHint>{fieldState.error.message}</ErrorHint>
              )}
            </>
          )}
          rules={{
            validate: required
              ? (value) =>
                  (Array.isArray(value) && value.length > 0) ||
                  "At least one file is required"
              : undefined,
          }}
        />
      );

    default:
      return null;
  }
};

const ToolCallGroup: React.FC<{
  item: Extract<ProcessedDisplayItem, { kind: "tool-group" }>;
  sessionSubstatus: ActiveSubstatus | null;
}> = ({ item, sessionSubstatus }) => {
  const isParallel = item.toolCallsWithResults.length > 1;

  // The description is the LLM-facing tool copy from the system prompt. It is
  // already shown where the agent is configured (Tools section), so in the run
  // trace it only pushes the input/output tabs down.
  const toolCallItems = item.toolCallsWithResults.map((tcwr) => (
    <ToolCallAccordionItem
      key={tcwr.toolCall.id}
      showDescription={false}
      status={computeToolCallStatus(tcwr.toolResult, sessionSubstatus)}
      toolCall={tcwr.toolCall}
      toolResult={tcwr.toolResult}
    />
  ));

  return (
    <div className="space-y-2">
      {isParallel ? (
        <ParallelExecutionWrapper count={item.toolCallsWithResults.length}>
          {toolCallItems}
        </ParallelExecutionWrapper>
      ) : (
        toolCallItems
      )}
    </div>
  );
};

type SessionStatusDisplayProps = {
  sessionStatus: PlaygroundSessionState & { status: "active" };
  pausedToolCount: number;
  liveStatusKind: LiveStatusDetailKind | null;
};

const SessionStatusDisplay: React.FC<SessionStatusDisplayProps> = ({
  sessionStatus,
  pausedToolCount,
  liveStatusKind,
}) => {
  const { substatus } = sessionStatus;

  // Booting/Running: indigo spinner with rotating text
  if (substatus === "booting" || substatus === "running") {
    return (
      <ThinkingStatusIndicator
        liveStatusKind={liveStatusKind}
        substatus={substatus}
      />
    );
  }

  // Paused
  if (substatus === "paused") {
    const waitingText =
      pausedToolCount > 1
        ? "Waiting for the tool responses to proceed."
        : "Waiting for the tool response to proceed.";
    return (
      <StatusIndicator
        icon={<Icon icon={faPause} size="xs" />}
        statusText={waitingText}
      />
    );
  }

  return null;
};

const CATEGORY_REASON_LABEL: Record<TimekeepingCategory, string> = {
  [TimekeepingCategory.QUOTA_BACKOFF]: "provider rate limits",
  [TimekeepingCategory.LLM_PROVIDER_RETRY]: "provider retries",
  [TimekeepingCategory.POLLING_FOR_RESULT]: "waiting for a run to finish",
};

const isTimekeepingCategory = (key: string): key is TimekeepingCategory =>
  key in CATEGORY_REASON_LABEL;

type TraceInfoBannerProps = {
  runtimeByCategory: RuntimeByCategory;
};

const TraceInfoBanner: React.FC<TraceInfoBannerProps> = ({
  runtimeByCategory,
}) => {
  const activeEntries = Object.entries(runtimeByCategory).flatMap(
    ([key, summary]) => {
      if (!summary || !isTimekeepingCategory(key)) return [];
      const count = summary.count ?? 0;
      const totalMs = summary.total_ms ?? 0;
      if (count === 0 || totalMs === 0) return [];
      return [{ category: key, count, totalMs }];
    },
  );

  if (activeEntries.length === 0) return null;

  const totalCount = activeEntries.reduce((sum, e) => sum + e.count, 0);
  const totalMs = activeEntries.reduce((sum, e) => sum + e.totalMs, 0);
  const reasonLabels = activeEntries.map(
    (e) => CATEGORY_REASON_LABEL[e.category],
  );
  const reasonsText =
    reasonLabels.length === 1
      ? reasonLabels[0]
      : `${reasonLabels.slice(0, -1).join(", ")} and ${reasonLabels[reasonLabels.length - 1]}`;
  const retriesText = totalCount === 1 ? "1 retry" : `${totalCount} retries`;

  return (
    <div className="mt-4">
      <Callout dataLoc="trace-info-banner" variant="neutral">
        This run waited{" "}
        <span className="font-inter-medium-12px text-gray-800">
          {formatDuration(totalMs)}
        </span>{" "}
        across{" "}
        <span className="font-inter-medium-12px text-gray-800">
          {retriesText}
        </span>{" "}
        due to {reasonsText}.
      </Callout>
    </div>
  );
};

const ParallelExecutionWrapper: React.FC<{
  count: number;
  children: React.ReactNode;
}> = ({ count, children }) => {
  return (
    <div>
      <div className="flex items-center gap-2 pb-2">
        <span className="font-inter-normal-12px text-gray-500">
          Parallel execution ({count} tools)
        </span>
      </div>
      <div className="space-y-4 border-gray-200 border-l pl-3">{children}</div>
    </div>
  );
};

const THINKING_VERBS = [
  "Thinking...",
  "Cogitating...",
  "Working...",
  "Wondering...",
  "Reflecting...",
  "Musing...",
  "Pondering...",
];
const VERB_ROTATION_INTERVAL_MS = 4500;

type ThinkingStatusIndicatorProps = {
  substatus: ActiveSubstatus;
  liveStatusKind: LiveStatusDetailKind | null;
};

const ThinkingStatusIndicator: React.FC<ThinkingStatusIndicatorProps> = ({
  substatus,
  liveStatusKind,
}) => {
  const [verbIndex, setVerbIndex] = useState(0);
  const isBooting = substatus === "booting";
  const isRetrying = liveStatusKind === "retrying";

  useEffect(() => {
    if (isBooting || isRetrying) {
      setVerbIndex(0);
      return;
    }

    const interval = setInterval(() => {
      setVerbIndex((prev) => (prev + 1) % THINKING_VERBS.length);
    }, VERB_ROTATION_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [isBooting, isRetrying]);

  const getStatusText = () => {
    if (isRetrying) return "Waiting for the rate limit to lift...";
    if (isBooting) return "Booting...";
    return THINKING_VERBS[verbIndex];
  };

  const icon = isRetrying ? (
    <Icon icon={faPause} size="xs" />
  ) : (
    <Icon variant="active" icon={faCircleNotch} size="xs" spin />
  );

  return <StatusIndicator icon={icon} statusText={getStatusText()} />;
};

type StatusIndicatorProps = {
  statusText: string;
  icon: React.ReactNode;
};

const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  statusText,
  icon,
}) => {
  return (
    <m.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0 }}
      transition={{ duration: 0.15, delay: 0.1, ease: "easeOut" }}
    >
      <div className="flex items-center gap-1.5">
        {icon}
        <AnimatePresence mode="wait">
          <m.span
            key={statusText}
            animate={{ opacity: 1 }}
            className="font-inter-normal-12px text-gray-800"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: "easeInOut" }}
          >
            {statusText}
          </m.span>
        </AnimatePresence>
      </div>
    </m.div>
  );
};

const AgentOutputStatusPill: React.FC<{ substatus: StoppedSubstatus }> = ({
  substatus,
}) => {
  if (substatus === "success") {
    return (
      <Pill size="sm" variant="green">
        <Pill.Icon icon={faCheck} />
        <Pill.Text>Successful</Pill.Text>
      </Pill>
    );
  }
  if (substatus === "failed") {
    return (
      <Pill size="sm" variant="red">
        <Pill.Icon icon={faExclamationCircle} />
        <Pill.Text>Failed</Pill.Text>
      </Pill>
    );
  }
  return (
    <Pill size="sm" variant="gray">
      <Pill.Icon icon={faStop} />
      <Pill.Text>Cancelled</Pill.Text>
    </Pill>
  );
};

const AgentOutputAccordionBody: React.FC<{
  result: SessionResultResponse;
}> = ({ result }) => {
  return (
    <div className="min-w-0 px-3 pb-3">
      {result.structured_output ? (
        <CollapsiblePre title="Agent Output">
          {JSON.stringify(result.structured_output, null, 2)}
        </CollapsiblePre>
      ) : (
        result.text && <MarkdownRenderer content={result.text} />
      )}
    </div>
  );
};
